import { Component, type ReactNode } from 'react';

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

/**
 * Catches render crashes so a bug never blanks the whole app into a silent
 * black page again — it shows what went wrong instead.
 */
export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: unknown): void {
    console.error('Gauntlet crashed:', error, info);
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex min-h-dvh items-center justify-center bg-void px-4">
          <div className="w-full max-w-lg space-y-3 rounded-2xl border border-loss/40 bg-loss/10 p-5">
            <h1 className="text-lg font-semibold text-ink">Something crashed</h1>
            <p className="text-sm text-ink-dim">
              Reload to continue. The error below is what needs fixing.
            </p>
            <pre className="max-h-64 overflow-auto whitespace-pre-wrap break-all rounded-lg border border-hairline bg-abyss p-3 text-xs text-ink-dim">
              {this.state.error.message}
              {'\n\n'}
              {this.state.error.stack}
            </pre>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="rounded-lg bg-brass px-4 py-2 text-sm font-medium text-void"
            >
              Reload
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
